import React from 'react';
import { X, Upload, Plus, Trash2, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const ServiceForm = ({
  formData,
  setFormData,
  editingService,
  activeTab,
  setActiveTab,
  imagePreview,
  fileInputRef,
  onImageUpload,
  onSubmit,
  onClose,
  isSubmitting
}) => {
  const handleFeatureChange = (lang, index, value) => {
    const key = `features_${lang}`;
    const newFeatures = [...formData[key]];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, [key]: newFeatures }));
  };

  const addFeature = (lang) => {
    const key = `features_${lang}`;
    setFormData(prev => ({ ...prev, [key]: [...prev[key], ''] }));
  };

  const removeFeature = (lang, index) => {
    const key = `features_${lang}`;
    const newFeatures = formData[key].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [key]: newFeatures.length ? newFeatures : [''] }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">
            {editingService ? 'Edit Service' : 'Add New Service'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Language Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-2">
            <button
              type="button"
              onClick={() => setActiveTab('en')}
              className={`px-4 py-2 font-medium rounded-t-lg ${activeTab === 'en' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}
            >
              English
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tr')}
              className={`px-4 py-2 font-medium rounded-t-lg flex items-center gap-2 ${activeTab === 'tr' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}
            >
              Türkçe
              {!formData.title_tr && (
                <span className="w-2 h-2 bg-orange-500 rounded-full" title="Turkish content missing"></span>
              )}
            </button>
          </div>

          {/* Warning if Turkish content is missing */}
          {activeTab === 'tr' && !formData.title_tr && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
              <strong>Note:</strong> Turkish content is empty. Fill in Turkish fields so the service displays correctly when visitors select Turkish language.
            </div>
          )}

          {/* Title & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title ({activeTab.toUpperCase()}) *
              </label>
              <Input
                value={formData[`title_${activeTab}`]}
                onChange={(e) => setFormData(prev => ({ ...prev, [`title_${activeTab}`]: e.target.value }))}
                placeholder="Service title"
                required={activeTab === 'en'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category ({activeTab.toUpperCase()}) *
              </label>
              <Input
                value={formData[`category_${activeTab}`]}
                onChange={(e) => setFormData(prev => ({ ...prev, [`category_${activeTab}`]: e.target.value }))}
                placeholder="e.g., Coaching, Training"
                required={activeTab === 'en'}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description ({activeTab.toUpperCase()}) *
            </label>
            <Textarea
              value={formData[`description_${activeTab}`]}
              onChange={(e) => setFormData(prev => ({ ...prev, [`description_${activeTab}`]: e.target.value }))}
              placeholder="Describe the service..."
              rows={4}
              required={activeTab === 'en'}
            />
          </div>

          {/* Price & Type */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (TL) *</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="999"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price Type *</label>
              <select
                value={formData.price_type}
                onChange={(e) => setFormData(prev => ({ ...prev, price_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="one-time">One-time</option>
                <option value="subscription">Subscription</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
              <select
                value={formData.service_type}
                onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="corporate">Corporate</option>
                <option value="individual">Individual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <Input
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                placeholder="e.g., 3 months"
              />
            </div>
          </div>

          {/* Subscription Period (conditional) */}
          {formData.price_type === 'subscription' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Period *</label>
              <select
                value={formData.subscription_period}
                onChange={(e) => setFormData(prev => ({ ...prev, subscription_period: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select period</option>
                <option value="month">Monthly</option>
                <option value="quarter">Quarterly</option>
                <option value="year">Yearly</option>
              </select>
            </div>
          )}

          {/* Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Features ({activeTab.toUpperCase()})
            </label>
            <div className="space-y-2">
              {formData[`features_${activeTab}`].map((feature, index) => (
                <div key={`feature-${activeTab}-${index}`} className="flex gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => handleFeatureChange(activeTab, index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeFeature(activeTab, index)}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addFeature(activeTab)}
                className="mt-2"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Feature
              </Button>
            </div>
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Image</label>
            <div className="flex items-start gap-4">
              <div 
                className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Image className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                    <span className="text-xs text-gray-500">Upload</span>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onImageUpload}
                className="hidden"
              />
              <div className="text-sm text-gray-500">
                <p>Click to upload an image</p>
                <p className="text-xs">PNG, JPG up to 5MB</p>
              </div>
            </div>
          </div>

          {/* Order & Toggles */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <Input
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                className="w-4 h-4 rounded text-blue-600"
              />
              <label htmlFor="featured" className="text-sm font-medium text-gray-700">Featured</label>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                className="w-4 h-4 rounded text-blue-600"
              />
              <label htmlFor="active" className="text-sm font-medium text-gray-700">Active</label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingService ? 'Update Service' : 'Create Service')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceForm;
